/**
 * 사용자 활동 추적 시스템
 * 
 * 기능:
 * - 활동 로그 기록 (activityLogs)
 * - 로그인 히스토리 기록 (loginHistory)
 * - 세션 관리 및 체류 시간 측정 (sessions)
 * - 활동 통계 요약 업데이트
 */

import { useUserStore } from "@/store/user";

// ============================================
// 상수 정의
// ============================================

/**
 * 활동 유형 상수
 */
export const ACTIVITY_TYPES = {
  // 페이지 조회
  PAGE_VIEW: "PAGE_VIEW",
  LIVE_VIEW: "LIVE_VIEW",
  ARTIST_VIEW: "ARTIST_VIEW",
  VENUE_VIEW: "VENUE_VIEW",
  VIDEO_VIEW: "VIDEO_VIEW",
  BOARD_VIEW: "BOARD_VIEW",
  PROFILE_VIEW: "PROFILE_VIEW",
  
  // 검색
  SEARCH: "SEARCH",
  FILTER_APPLY: "FILTER_APPLY",
  
  // 라이브 인터랙션
  LIVE_LIKE: "LIVE_LIKE",
  LIVE_UNLIKE: "LIVE_UNLIKE",
  
  // 아티스트 인터랙션
  ARTIST_LIKE: "ARTIST_LIKE",
  ARTIST_UNLIKE: "ARTIST_UNLIKE",
  
  // 비디오 인터랙션
  VIDEO_LIKE: "VIDEO_LIKE",
  VIDEO_UNLIKE: "VIDEO_UNLIKE",
  VIDEO_COMMENT: "VIDEO_COMMENT",
  VIDEO_PLAY: "VIDEO_PLAY", // 임베드 유튜브 재생
  
  // 공유 인터랙션
  LIVE_SHARE: "LIVE_SHARE",
  ARTIST_SHARE: "ARTIST_SHARE",
  POST_SHARE: "POST_SHARE",
  VIDEO_SHARE: "VIDEO_SHARE",
  VENUE_SHARE: "VENUE_SHARE",
  
  // 댓글 인터랙션
  POST_COMMENT: "POST_COMMENT",
  
  // 필터 사용
  DATE_FILTER: "DATE_FILTER",
  
  // 콘텐츠 생성
  LIVE_CREATE: "LIVE_CREATE",
  ARTIST_CREATE: "ARTIST_CREATE",
  POST_CREATE: "POST_CREATE",
  VIDEO_CREATE: "VIDEO_CREATE",
  
  // 콘텐츠 수정
  LIVE_UPDATE: "LIVE_UPDATE",
  ARTIST_UPDATE: "ARTIST_UPDATE",
  POST_UPDATE: "POST_UPDATE",
  
  // 프로필 활동
  NICKNAME_UPDATE: "NICKNAME_UPDATE",
  X_PROFILE_FETCH: "X_PROFILE_FETCH",
  ARTIST_CARD_UPDATE: "ARTIST_CARD_UPDATE",
  
  // 특수 페이지 조회
  CALENDAR_VIEW: "CALENDAR_VIEW",
  NOTIFICATIONS_VIEW: "NOTIFICATIONS_VIEW",
  
  // 상세 페이지 인터랙션
  DETAIL_LINK_CLICK: "DETAIL_LINK_CLICK",
  MAP_VIEW_CLICK: "MAP_VIEW_CLICK",
  
  // 알림/캘린더 인터랙션
  NOTIFICATION_READ_ALL: "NOTIFICATION_READ_ALL",
  NOTIFICATION_CLICK: "NOTIFICATION_CLICK",
  CALENDAR_LIVE_CLICK: "CALENDAR_LIVE_CLICK",
  
  // 게시글 인터랙션
  POST_LIKE: "POST_LIKE",
  POST_UNLIKE: "POST_UNLIKE",
  REPORT: "REPORT",
  
  // 전환
  TICKET_CLICK: "TICKET_CLICK",
  RESERVATION_START: "RESERVATION_START",
  RESERVATION_COMPLETE: "RESERVATION_COMPLETE",
  
  // 세션
  LOGIN: "LOGIN",
  LOGOUT: "LOGOUT",
  SESSION_START: "SESSION_START",
  SESSION_END: "SESSION_END",
};

// 페이지 추적 변수
let pageEnterTime = null;
let currentPage = null;
let currentPageId = null;

// 익명 세션 ID (비로그인 사용자용)
let anonymousSessionId = null;

/**
 * 익명 세션 ID 가져오기 (없으면 생성)
 */
function getAnonymousSessionId() {
  if (anonymousSessionId) return anonymousSessionId;
  
  // sessionStorage에서 기존 ID 확인
  const storedId = sessionStorage.getItem("linkook_anonymous_session");
  if (storedId) {
    anonymousSessionId = storedId;
    return anonymousSessionId;
  }
  
  // 새 ID 생성
  anonymousSessionId = `anon_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  sessionStorage.setItem("linkook_anonymous_session", anonymousSessionId);
  return anonymousSessionId;
}

// ============================================
// 디바이스 정보 감지
// ============================================

/**
 * 디바이스 정보 가져오기
 */
export function getDeviceInfo() {
  const userAgent = navigator.userAgent;
  
  // 기기 타입
  let device = "Desktop";
  if (/Mobile|Android|iPhone|iPad|iPod/i.test(userAgent)) {
    if (/iPad/i.test(userAgent)) {
      device = "iPad";
    } else if (/iPhone/i.test(userAgent)) {
      device = "iPhone";
    } else if (/Android/i.test(userAgent)) {
      device = /Mobile/i.test(userAgent) ? "Android Phone" : "Android Tablet";
    } else {
      device = "Mobile";
    }
  }
  
  // 브라우저
  let browser = "Unknown";
  if (/Chrome/i.test(userAgent) && !/Edg/i.test(userAgent)) {
    browser = "Chrome";
  } else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) {
    browser = "Safari";
  } else if (/Firefox/i.test(userAgent)) {
    browser = "Firefox";
  } else if (/Edg/i.test(userAgent)) {
    browser = "Edge";
  } else if (/MSIE|Trident/i.test(userAgent)) {
    browser = "IE";
  } else if (/Whale/i.test(userAgent)) {
    browser = "Whale";
  } else if (/SamsungBrowser/i.test(userAgent)) {
    browser = "Samsung Browser";
  }
  
  // 운영체제
  let os = "Unknown";
  if (/Windows NT 10/i.test(userAgent)) {
    os = "Windows 10/11";
  } else if (/Windows NT/i.test(userAgent)) {
    os = "Windows";
  } else if (/Mac OS X/i.test(userAgent)) {
    const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
    os = match ? `macOS ${match[1].replace("_", ".")}` : "macOS";
  } else if (/iPhone OS (\d+)/i.test(userAgent)) {
    const match = userAgent.match(/iPhone OS (\d+)/);
    os = match ? `iOS ${match[1]}` : "iOS";
  } else if (/Android (\d+)/i.test(userAgent)) {
    const match = userAgent.match(/Android (\d+)/);
    os = match ? `Android ${match[1]}` : "Android";
  } else if (/Linux/i.test(userAgent)) {
    os = "Linux";
  }
  
  // 화면 크기
  const screenSize = `${window.screen.width}x${window.screen.height}`;
  
  return {
    device,
    browser,
    os,
    screenSize,
    userAgent: userAgent.substring(0, 200), // 너무 길면 자르기
  };
}

// ============================================
// 세션 ID 관리 (sessionStorage 기반)
// ============================================

/**
 * 세션 ID 가져오기 (sessionStorage 기반, 새로고침 후에도 유지)
 */
function getSessionId() {
  const userStore = useUserStore();
  const isLoggedIn = !!userStore.email;
  
  if (isLoggedIn) {
    // 로그인 사용자: sessionStorage에 저장
    let sessionId = sessionStorage.getItem("linkook_session_id");
    
    if (!sessionId) {
      sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      sessionStorage.setItem("linkook_session_id", sessionId);
    }
    
    return sessionId;
  } else {
    // 비로그인 사용자: 기존 익명 세션 ID 사용
    return getAnonymousSessionId();
  }
}

// ============================================
// 활동 로그 기록
// ============================================

/**
 * 활동 로그 기록 (백엔드 API 호출)
 * @param {string} type - 활동 유형 (ACTIVITY_TYPES 참조)
 * @param {Object} options - 옵션
 * @param {string} options.target - 대상 페이지/기능
 * @param {string} options.targetId - 대상 ID
 * @param {number} options.duration - 체류 시간 (초)
 * @param {string} options.referrer - 이전 페이지
 * @param {Object} options.metadata - 추가 메타데이터
 */
export async function trackActivity(type, options = {}) {
  // write/user.js의 trackActivityLog 함수 사용 (API 통합)
  const { trackActivityLog } = await import("@/utils/write/user");
  const sessionId = getSessionId();
  await trackActivityLog(type, { ...options, sessionId });
}

// ============================================
// 페이지 체류 시간 측정
// ============================================

// 현재 페이지의 활동 타입 저장
let currentActivityType = ACTIVITY_TYPES.PAGE_VIEW;

/**
 * 페이지 진입 기록
 * @param {string} pageName - 페이지 이름
 * @param {string} pageId - 페이지 ID (optional)
 * @param {string} activityType - 활동 타입 (optional, 기본값: PAGE_VIEW)
 */
export function enterPage(pageName, pageId = null, activityType = ACTIVITY_TYPES.PAGE_VIEW) {
  // 이전 페이지 체류 시간 기록
  if (currentPage && pageEnterTime) {
    const duration = Math.floor((Date.now() - pageEnterTime) / 1000);
    if (duration > 0 && duration < 3600) { // 1시간 이상은 비정상으로 간주
      trackActivity(currentActivityType, {
        target: currentPage,
        targetId: currentPageId,
        duration: duration,
      });
    }
  }
  
  // 새 페이지 진입
  currentPage = pageName;
  currentPageId = pageId;
  currentActivityType = activityType;
  pageEnterTime = Date.now();
}

/**
 * 페이지 이탈 기록 (beforeunload 시 호출)
 */
export function leavePage() {
  if (currentPage && pageEnterTime) {
    const duration = Math.floor((Date.now() - pageEnterTime) / 1000);
    if (duration > 0 && duration < 3600) {
      // 동기적으로 보내기 위해 sendBeacon 사용 (가능한 경우)
      trackActivity(currentActivityType, {
        target: currentPage,
        targetId: currentPageId,
        duration: duration,
      });
    }
  }
  
  currentPage = null;
  currentPageId = null;
  currentActivityType = ACTIVITY_TYPES.PAGE_VIEW;
  pageEnterTime = null;
}

// ============================================
// 로그인 히스토리 기록
// ============================================

/**
 * 로그인 활동 기록
 * @param {string} provider - 로그인 방식 (email, kakao, google 등)
 */
export async function trackLoginActivity(provider = "unknown") {
  // localhost에서는 추적하지 않음
  if (window.location.hostname.includes("localhost")) {
    return;
  }
  
  try {
    await trackActivity(ACTIVITY_TYPES.LOGIN, {
      metadata: { provider },
    });
  } catch (error) {
    console.error("로그인 활동 기록 오류:", error);
  }
}

// ============================================
// 특정 활동 추적 헬퍼 함수
// ============================================

/**
 * 공연 상세 조회 추적
 */
export function trackLiveView(liveId, liveTitle = null) {
  trackActivity(ACTIVITY_TYPES.LIVE_VIEW, {
    target: "liveDetail",
    targetId: liveId,
    metadata: liveTitle ? { title: liveTitle } : undefined,
  });
}

/**
 * 아티스트 상세 조회 추적
 */
export function trackArtistView(artistName) {
  trackActivity(ACTIVITY_TYPES.ARTIST_VIEW, {
    target: "artistDetail",
    targetId: artistName,
  });
}

/**
 * 공연장 상세 조회 추적
 */
export function trackVenueView(venueName) {
  trackActivity(ACTIVITY_TYPES.VENUE_VIEW, {
    target: "venueDetail",
    targetId: venueName,
  });
}

/**
 * 검색 추적
 */
export function trackSearch(query, resultCount = null) {
  trackActivity(ACTIVITY_TYPES.SEARCH, {
    metadata: {
      query: query,
      resultCount: resultCount,
    },
  });
}

/**
 * 찜 추적
 */
export function trackLike(targetType, targetId) {
  let activityType;
  switch (targetType) {
    case "live":
      activityType = ACTIVITY_TYPES.LIVE_LIKE;
      break;
    case "artist":
      activityType = ACTIVITY_TYPES.ARTIST_LIKE;
      break;
    case "post":
    case "board":
      activityType = ACTIVITY_TYPES.POST_LIKE;
      break;
    default:
      activityType = ACTIVITY_TYPES.LIVE_LIKE; // 기본값
  }
  trackActivity(activityType, {
    target: targetType,
    targetId: targetId,
  });
}

/**
 * 찜 취소 추적
 */
export function trackUnlike(targetType, targetId) {
  let activityType;
  switch (targetType) {
    case "live":
      activityType = ACTIVITY_TYPES.LIVE_UNLIKE;
      break;
    case "artist":
      activityType = ACTIVITY_TYPES.ARTIST_UNLIKE;
      break;
    case "post":
    case "board":
      activityType = ACTIVITY_TYPES.POST_UNLIKE;
      break;
    default:
      activityType = ACTIVITY_TYPES.LIVE_UNLIKE; // 기본값
  }
  trackActivity(activityType, {
    target: targetType,
    targetId: targetId,
  });
}

/**
 * 공유 추적
 */
export function trackShare(targetType, targetId, platform = null) {
  let activityType;
  switch (targetType) {
    case "live":
      activityType = ACTIVITY_TYPES.LIVE_SHARE;
      break;
    case "artist":
      activityType = ACTIVITY_TYPES.ARTIST_SHARE;
      break;
    case "post":
    case "board":
      activityType = ACTIVITY_TYPES.POST_SHARE;
      break;
    case "video":
      activityType = ACTIVITY_TYPES.VIDEO_SHARE;
      break;
    case "venue":
      activityType = ACTIVITY_TYPES.VENUE_SHARE;
      break;
    default:
      activityType = ACTIVITY_TYPES.LIVE_SHARE; // 기본값
  }
  trackActivity(activityType, {
    target: targetType,
    targetId: targetId,
    metadata: platform ? { platform } : undefined,
  });
}

/**
 * 댓글 작성 추적
 */
export function trackComment(targetType, targetId) {
  trackActivity(ACTIVITY_TYPES.POST_COMMENT, {
    target: targetType,
    targetId: targetId,
  });
}

/**
 * 예매 클릭 추적
 */
export function trackTicketClick(liveId, ticketType = null) {
  trackActivity(ACTIVITY_TYPES.TICKET_CLICK, {
    target: "ticket",
    targetId: liveId,
    metadata: ticketType ? { type: ticketType } : undefined,
  });
}

/**
 * 예매 완료 추적
 */
export function trackReservationComplete(liveId) {
  trackActivity(ACTIVITY_TYPES.RESERVATION_COMPLETE, {
    target: "ticket",
    targetId: liveId,
  });
}

// ============================================
// 유입 경로 추적
// ============================================

/**
 * UTM 파라미터 파싱
 */
function parseUtmParams() {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get("utm_source") || null,
    utm_medium: urlParams.get("utm_medium") || null,
    utm_campaign: urlParams.get("utm_campaign") || null,
    utm_term: urlParams.get("utm_term") || null,
    utm_content: urlParams.get("utm_content") || null,
  };
}

/**
 * Referrer 분석
 */
function analyzeReferrer() {
  const referrer = document.referrer;
  
  if (!referrer) {
    return { source: "direct", referrer: null, category: "직접 접속" };
  }
  
  try {
    const referrerUrl = new URL(referrer);
    const hostname = referrerUrl.hostname.toLowerCase();
    
    // 같은 사이트 내 이동은 제외
    if (hostname === window.location.hostname) {
      return { source: "internal", referrer: null, category: "내부 이동" };
    }
    
    // 검색 엔진
    if (hostname.includes("google.")) {
      return { source: "google", referrer: hostname, category: "검색 엔진" };
    }
    if (hostname.includes("naver.com")) {
      return { source: "naver", referrer: hostname, category: "검색 엔진" };
    }
    if (hostname.includes("daum.net") || hostname.includes("kakao.com")) {
      return { source: "daum/kakao", referrer: hostname, category: "검색 엔진" };
    }
    if (hostname.includes("bing.com")) {
      return { source: "bing", referrer: hostname, category: "검색 엔진" };
    }
    
    // SNS
    if (hostname.includes("twitter.com") || hostname.includes("x.com") || hostname.includes("t.co")) {
      return { source: "twitter", referrer: hostname, category: "SNS" };
    }
    if (hostname.includes("instagram.com")) {
      return { source: "instagram", referrer: hostname, category: "SNS" };
    }
    if (hostname.includes("facebook.com") || hostname.includes("fb.com")) {
      return { source: "facebook", referrer: hostname, category: "SNS" };
    }
    if (hostname.includes("youtube.com") || hostname.includes("youtu.be")) {
      return { source: "youtube", referrer: hostname, category: "SNS" };
    }
    if (hostname.includes("threads.net")) {
      return { source: "threads", referrer: hostname, category: "SNS" };
    }
    if (hostname.includes("tiktok.com")) {
      return { source: "tiktok", referrer: hostname, category: "SNS" };
    }
    
    // 메신저
    if (hostname.includes("kakaotalk") || hostname.includes("kakao.com")) {
      return { source: "kakaotalk", referrer: hostname, category: "메신저" };
    }
    if (hostname.includes("line.me")) {
      return { source: "line", referrer: hostname, category: "메신저" };
    }
    if (hostname.includes("discord.com") || hostname.includes("discord.gg")) {
      return { source: "discord", referrer: hostname, category: "메신저" };
    }
    
    // 기타 외부 사이트
    return { source: "referral", referrer: hostname, category: "외부 링크" };
    
  } catch (error) {
    return { source: "unknown", referrer: referrer.substring(0, 100), category: "알 수 없음" };
  }
}

/**
 * 유입 경로 정보 수집
 */
export function getTrafficSource() {
  const utmParams = parseUtmParams();
  const referrerInfo = analyzeReferrer();
  const landingPage = window.location.pathname + window.location.search;
  
  // UTM 파라미터가 있으면 우선 사용
  if (utmParams.utm_source) {
    return {
      source: utmParams.utm_source,
      medium: utmParams.utm_medium || "unknown",
      campaign: utmParams.utm_campaign,
      term: utmParams.utm_term,
      content: utmParams.utm_content,
      category: "캠페인",
      referrer: referrerInfo.referrer,
      landingPage: landingPage,
    };
  }
  
  // UTM이 없으면 referrer 기반
  return {
    source: referrerInfo.source,
    medium: referrerInfo.category === "검색 엔진" ? "organic" : 
            referrerInfo.category === "SNS" ? "social" :
            referrerInfo.category === "직접 접속" ? "direct" : "referral",
    campaign: null,
    term: null,
    content: null,
    category: referrerInfo.category,
    referrer: referrerInfo.referrer,
    landingPage: landingPage,
  };
}

/**
 * 유입 경로 기록 (첫 방문 시)
 */
export async function recordTrafficSource() {
  // write/user.js의 API 함수 사용
  const { recordTrafficSource: recordTrafficSourceApi } = await import("@/utils/write/user");
  return await recordTrafficSourceApi();
}
/**
 * 유입 경로 통계 조회 (관리자용)
 * @param {number|object} daysOrOptions - 조회할 기간 (일) 또는 { startDate, endDate } 객체
 * @returns {Promise<Object>} { data: Array, stats: Object } 형태의 데이터
 */
export async function getTrafficStats(daysOrOptions = 30) {
  // read/user.js의 API 함수 사용
  const { getTrafficStats: getTrafficStatsApi } = await import("@/utils/read/user");
  return await getTrafficStatsApi(daysOrOptions);
}

/**
 * 익명 사용자 활동 로그 조회 (관리자용)
 * @param {number|object} daysOrOptions - 조회할 기간 (일) 또는 { startDate, endDate } 객체
 * @returns {Promise<Object>} { data: Array, stats: Object } 형태의 데이터
 */
export async function getAnonymousActivityStats(daysOrOptions = 30) {
  // read/user.js의 API 함수 사용
  const { getAnonymousActivityStats: getAnonymousActivityStatsApi } = await import("@/utils/read/user");
  return await getAnonymousActivityStatsApi(daysOrOptions);
}

/**
 * 통합 활동 통계 조회 (로그인 + 비로그인, 사용자 식별 없이 통계만)
 * @param {number|object} daysOrOptions - 조회할 기간 (일) 또는 { startDate, endDate } 객체
 * @returns {Promise<Object>} 통계 데이터
 */
export async function getAllActivityStats(daysOrOptions = 30) {
  // read/user.js의 API 함수 사용
  const { getAllActivityStats: getAllActivityStatsApi } = await import("@/utils/read/user");
  const stats = await getAllActivityStatsApi(daysOrOptions);
  return { stats };
}

// ============================================
// 초기화 및 정리
// ============================================

/**
 * 활동 추적 초기화 (앱 시작 시 호출)
 */
export async function initActivityTracker() {
  // 유입 경로 기록 (첫 방문 시)
  await recordTrafficSource();
  
  // 페이지 이탈 시 기록
  window.addEventListener("beforeunload", () => {
    leavePage();
  });
  
  // visibilitychange 이벤트로 탭 전환 감지
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      // 탭이 백그라운드로 갈 때
      leavePage();
    }
  });
}

/**
 * 활동 추적 정리 (앱 종료 시 호출)
 */
export function cleanupActivityTracker() {
  leavePage();
}

